import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TrpcService } from '../../services/trpc/trpc.service';
import { AuthService, CookieData } from '../../services/auth/auth.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-test-login',
  templateUrl: './test-login.component.html',
  styleUrls: ['./test-login.component.scss']
})
export class TestLoginComponent {
  loginForm: FormGroup;
  errorMessage: string = '';
  successMessage: string = '';
  currentUserInfo: any = null;
  isLoading: boolean = false;
  isVerifyingAuth: boolean = false;

  // For testing automatically use these credentials on the form
  TEST_DEFAULT_USERNAME = 'srptest';
  TEST_DEFAULT_PASSWORD = '4EHqnvJRkr$xh';
  TEST_DEFAULT_LOCATION = '99998';

  constructor(
    private formBuilder: FormBuilder,
    private trpcService: TrpcService,
    private authService: AuthService,
    private router: Router
  ) {
    this.loginForm = this.formBuilder.group({
      username: [this.TEST_DEFAULT_USERNAME, [Validators.required]],
      password: [this.TEST_DEFAULT_PASSWORD, [Validators.required]],
      locationId: [this.TEST_DEFAULT_LOCATION, [Validators.required]]
    });
  }

  async onSubmit() {
    if (this.loginForm.invalid) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { username, password, locationId } = this.loginForm.value;

      const result: any = await this.trpcService.trpc.authRouter.loginNG.mutate({
        username,
        password,
        locationId
      });

      console.log('Login successful:', result);

      // Display detailed user information
      const userInfo = result.user;
      const rolesText = userInfo.roles ? userInfo.roles.join(', ') : 'No roles';

      // Set the cookie data in AuthService and TrpcService if session info is available
      if (userInfo.sessionName && userInfo.sessionId) {
        const cookieData: CookieData = {
          cookie: {
            name: userInfo.sessionName,
            value: userInfo.sessionId
          }
        };

        // Store the cookie data in AuthService and localStorage
        this.authService.saveCookieToStorage(cookieData);

        // Update TrpcService with the new token
        this.trpcService.setToken(cookieData);

        console.log('Authentication cookie set and saved to localStorage:', {
          name: userInfo.sessionName,
          value: userInfo.sessionId
        });
      }

      // Build success message
      let successDetails = `User: ${userInfo.username} | UID: ${userInfo.uid} | Email: ${userInfo.email} | Roles: ${rolesText}`;

      // Add location information
      if (result.location_id) {
        successDetails += ` | Location: ${result.location_id}`;
      }

      // Add session information if available
      if (userInfo.sessionName && userInfo.sessionId) {
        successDetails += ` | Session: ${userInfo.sessionName}`;
        this.successMessage = successDetails + ' | Authentication cookie has been set. You are now logged in!';

        // Add webconnect response if available
        if (result.webconnect_response) {
          this.successMessage += `\n\nWebConnect: ${result.webconnect_response}`;
        }

        // Now verify the authentication by calling getCurrentUser
        this.verifyAuthentication();
      } else {
        this.successMessage = successDetails + ' | Warning: No session cookie was returned. You may not be fully authenticated.';
      }

      // Hide the loading state
      this.isLoading = false;

    } catch (error: any) {
      console.error('Test login failed:', error);
      this.errorMessage = error.message || 'Login failed. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  navigateToDashboard() {
    console.log('Navigating to dashboard with authentication:', {
      cookieData: this.authService.cookieData,
      hasToken: !!this.authService.cookieData?.cookie?.value
    });
    this.router.navigate(['/dashboard']);
  }

  async verifyAuthentication() {
    this.isVerifyingAuth = true;

    try {
      console.log('Verifying authentication by calling getCurrentUser...');

      // Call the getCurrentUser endpoint to verify authentication
      const currentUser = await this.trpcService.trpc.authRouter.getCurrentUser.query();

      console.log('getCurrentUser response:', currentUser);

      // Store the current user information
      this.currentUserInfo = currentUser;

      // Update success message with verification
      this.successMessage += '\n\n✅ Authentication verified! getCurrentUser API call successful.';

    } catch (error: any) {
      console.error('Failed to get current user:', error);
      this.currentUserInfo = {
        error: error.message || 'Failed to verify authentication'
      };
    } finally {
      this.isVerifyingAuth = false;
    }
  }
}
